import React, {
	createContext,
	useContext,
	useEffect,
	useRef,
	useState
} from 'react';
import PropTypes from 'prop-types';
import { useSiteConfig } from './useSiteConfig';
import { COOKIE_CONSENT, STORAGE } from '../helpers/enums';

const TelemetryContext = createContext();

export const useTelemetry = () => {
	const context = useContext(TelemetryContext);
	if (!context) {
		throw new Error('useTelemetry must be used within TelemetryProvider');
	}
	return context;
};

export const TelemetryProvider = ({ children, userId }) => {
	const [consentStatus, setConsentStatus] = useState(COOKIE_CONSENT.PENDING);
	const [locationData, setLocationData] = useState(null);
	const [sessionId, setSessionId] = useState('');
	const { siteConfig } = useSiteConfig();
	const clickQueue = useRef([]);
	const batchTimer = useRef(null);

	// Check if we're in a browser environment
	const isBrowser = typeof window !== 'undefined';

	// Generate or retrieve session ID safely
	useEffect(() => {
		if (!isBrowser) return;
		// eslint-disable-next-line
		let savedSessionId = sessionStorage.getItem(STORAGE.TELEMETRY_SESSION_ID);
		if (!savedSessionId) {
			savedSessionId =
				'sess_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
			// eslint-disable-next-line
			sessionStorage.setItem(STORAGE.TELEMETRY_SESSION_ID, savedSessionId);
		}
		setSessionId(savedSessionId);

		// Check for existing consent
		// eslint-disable-next-line
		const savedConsent = localStorage.getItem(STORAGE.COOKIE_CONSENT);
		if (savedConsent) {
			setConsentStatus(savedConsent);
			if (savedConsent === COOKIE_CONSENT.ACCEPTED) {
				fetchLocationData();
			}
		}
	}, []);

	// Fetch location data when consent is given
	const fetchLocationData = async () => {
		if (!siteConfig.telemetry.active) return;
		if (!isBrowser) return;

		try {
			// Use privacy-respecting geolocation service
			const response = await fetch('/api/location');
			if (response.ok) {
				const data = await response.json();
				setLocationData({
					country: data.country,
					region: data.region,
					timezone: data.timezone
				});
			}
		} catch (error) {
			console.log('Location services unavailable');
		}
	};

	// Handle consent changes
	const handleConsentChange = (newConsentStatus) => {
		if (!isBrowser) return;

		setConsentStatus(newConsentStatus);
		// eslint-disable-next-line
		localStorage.setItem(STORAGE.COOKIE_CONSENT, newConsentStatus);

		if (newConsentStatus === COOKIE_CONSENT.ACCEPTED) {
			fetchLocationData();
		}
	};

	// Batch and send telemetry
	const flushQueue = async () => {
		if (!siteConfig.telemetry.active) return;
		if (clickQueue.current.length === 0) return;

		const events = [...clickQueue.current];
		clickQueue.current = [];

		try {
			await fetch('/api/telemetry', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-Session-ID': sessionId
				},
				body: JSON.stringify({
					userId: consentStatus === COOKIE_CONSENT.ACCEPTED ? userId : null,
					consentStatus,
					locationData:
						consentStatus === COOKIE_CONSENT.ACCEPTED ? locationData : null,
					events,
					timestamp: Date.now()
				})
			});
		} catch (error) {
			console.warn('Telemetry send failed:', error);
			// Re-queue for retry
			clickQueue.current.push(...events);
		}
	};

	// Track clicks with consent awareness
	const trackClick = (element, metadata = {}) => {
		if (!siteConfig.telemetry.active) return;
		if (consentStatus === COOKIE_CONSENT.REJECTED || !isBrowser) return;

		const event = {
			type: 'click',
			element,
			metadata,
			timestamp: Date.now(),
			page: window.location.pathname,
			userAgent: navigator.userAgent
		};

		clickQueue.current.push(event);

		// Batch every 5 seconds or when queue hits 10 items
		if (clickQueue.current.length >= 10) {
			flushQueue();
		} else {
			clearTimeout(batchTimer.current);
			batchTimer.current = setTimeout(flushQueue, 5000);
		}
	};

	// Track any event with consent awareness
	const trackEvent = (eventType, data = {}) => {
		if (!siteConfig.telemetry.active) return;
		if (consentStatus === COOKIE_CONSENT.REJECTED || !isBrowser) return;

		const event = {
			type: eventType,
			...data,
			timestamp: Date.now(),
			page: window.location.pathname,
			sessionId,
			userId: consentStatus === COOKIE_CONSENT.ACCEPTED ? userId : null,
			locationData:
				consentStatus === COOKIE_CONSENT.ACCEPTED ? locationData : null
		};

		clickQueue.current.push(event);

		if (clickQueue.current.length >= 10) {
			flushQueue();
		} else {
			clearTimeout(batchTimer.current);
			batchTimer.current = setTimeout(flushQueue, 5000);
		}
	};

	useEffect(() => {
		return () => {
			if (isBrowser) {
				clearTimeout(batchTimer.current);
				if (clickQueue.current.length > 0) {
					flushQueue();
				}
			}
		};
	}, []);

	return (
		<TelemetryContext.Provider
			value={{
				trackClick,
				trackEvent,
				consentStatus,
				handleConsentChange,
				locationData,
				sessionId
			}}
		>
			{children}
		</TelemetryContext.Provider>
	);
};

TelemetryProvider.propTypes = {
	children: PropTypes.node.isRequired,
	userId: PropTypes.string
};

const TelemetryContextType = PropTypes.shape({
	trackClick: PropTypes.func.isRequired,
	trackEvent: PropTypes.func.isRequired,
	consentStatus: PropTypes.oneOf([
		COOKIE_CONSENT.PENDING,
		COOKIE_CONSENT.ACCEPTED,
		COOKIE_CONSENT.REJECTED
	]).isRequired,
	handleConsentChange: PropTypes.func.isRequired,
	locationData: PropTypes.shape({
		country: PropTypes.string,
		region: PropTypes.string,
		timezone: PropTypes.string
	}),
	sessionId: PropTypes.string.isRequired
});

TelemetryContext.propTypes = TelemetryContextType;

export default TelemetryProvider;
